import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledChip = styled(Chip)(({ theme }) => ({
  height: 'auto',
  minHeight: 28,
  paddingTop: 2,
  paddingBottom: 2,
  border: 'none',
  '& .MuiChip-label': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontFamily: 'Merriweather',
    fontSize: '0.75rem',
  },
}));

/**
 * Chip with a small uppercase title and a prominent subtitle.
 * Optionally wraps in MUI Tooltip when `tooltip` is provided.
 *
 * @param {object} props
 * @param {React.ReactNode} [props.title] — small label (e.g. "Tactic"); omit for subtitle-only
 * @param {React.ReactNode} props.subtitle — main text
 * @param {React.ReactNode} [props.icon] — icon to display next to the title
 * @param {React.ReactNode} [props.tooltip] — Tooltip content; empty/absent = no Tooltip
 * @param {object} [props.tooltipProps] — passed to MUI Tooltip (`title` always comes from `tooltip`)
 * @param {object} [props.chipProps] — passed to MUI Chip (e.g. variant, color, sx, onClick)
 */
const TitleSubtitleChip = ({
  title,
  subtitle,
  icon,
  tooltip,
  tooltipProps = {},
  chipProps = {},
}) => {
  const label = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      {icon && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16 }}>
          {icon}
        </Box>
      )}
      {title != null && title !== '' && (
        <Typography
          component="span"
          sx={{
            fontWeight: 700,
            fontSize: '0.65rem',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            alignItems: 'center',
            color: 'text.secondary',
          }}
        >
          {title}
        </Typography>
      )}
      { subtitle && (
        <Typography
          component="span"
          sx={{
            fontFamily: 'MedievalSharp',
            fontSize: '0.65rem',
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  const chip = (
    <StyledChip label={label} size="small" {...chipProps} />
  );

  const showTooltip =
    tooltip != null &&
    tooltip !== '' &&
    !(typeof tooltip === 'string' && tooltip.trim() === '');

  if (!showTooltip) {
    return chip;
  }

  return (
    <Tooltip arrow placement="top" {...tooltipProps} title={tooltip}>
      {chip}
    </Tooltip>
  );
};

export default TitleSubtitleChip;
